import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, Mail, Copy, Check, ExternalLink, Loader2, AlertCircle, ChevronLeft, ChevronRight, ChevronDown, Globe, X } from 'lucide-react';
import apiClient from '../../api/apiClient';
import { ShowNotificationFn } from './crm.types';

interface CrmConfigurationProps {
  showNotification: ShowNotificationFn;
}

const WEBHOOK_ADDRESS = 'support@smart-resell.com';
const TOTAL_STEPS = 4;

const COUNTRIES = [
  'Allemagne', 'Autriche', 'Belgique', 'Croatie', 'Danemark',
  'Espagne', 'Estonie', 'Finlande', 'France', 'Grèce',
  'Hongrie', 'Irlande', 'Italie', 'Lettonie', 'Lituanie',
  'Luxembourg', 'Pays-Bas', 'Pologne', 'Portugal', 'Roumanie',
  'Royaume-Uni', 'Slovaquie', 'Slovénie', 'Suède', 'Tchèquie',
  'Autre',
];

const COUNTRY_TO_CODE: Record<string, string> = {
  'Allemagne': 'DE', 'Autriche': 'AT', 'Belgique': 'BE', 'Croatie': 'HR',
  'Danemark': 'DK', 'Espagne': 'ES', 'Estonie': 'EE', 'Finlande': 'FI',
  'France': 'FR', 'Grèce': 'GR', 'Hongrie': 'HU', 'Irlande': 'IE',
  'Italie': 'IT', 'Lettonie': 'LV', 'Lituanie': 'LT', 'Luxembourg': 'LU',
  'Pays-Bas': 'NL', 'Pologne': 'PL', 'Portugal': 'PT', 'Roumanie': 'RO',
  'Royaume-Uni': 'GB', 'Slovaquie': 'SK', 'Slovénie': 'SI', 'Suède': 'SE',
  'Tchèquie': 'CZ',
};

const CODE_TO_COUNTRY: Record<string, string> = Object.fromEntries(
  Object.entries(COUNTRY_TO_CODE).map(([name, code]) => [code, name])
);

const StepDot: React.FC<{ n: number; current: number; onClick?: () => void }> = ({ n, current, onClick }) => {
  const done = n < current;
  const active = n === current;
  const clickable = done;
  return (
    <div
      onClick={clickable ? onClick : undefined}
      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 transition-all ${
        done ? 'bg-emerald-500 text-white' : active ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-400'
      } ${clickable ? 'cursor-pointer hover:opacity-80' : ''}`}
    >
      {done ? <Check size={14} /> : n}
    </div>
  );
};

const CrmConfiguration: React.FC<CrmConfigurationProps> = ({ showNotification }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState('France');
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmStatus, setConfirmStatus] = useState<'pending' | 'received'>('pending');
  const [confirmUrl, setConfirmUrl] = useState<string | null>(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showAutreModal, setShowAutreModal] = useState(false);
  const [showEmailTakenModal, setShowEmailTakenModal] = useState(false);
  const [countryPickerOpen, setCountryPickerOpen] = useState(false);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    Promise.all([
      apiClient.get('/crm/settings'),
      apiClient.getMe(),
    ]).then(([settingsRes, me]) => {
      const s = settingsRes.data;
      // Restaurer le pays depuis la DB
      if (s.country) {
        setCountry(CODE_TO_COUNTRY[s.country] || 'France');
      } else if (me?.country) {
        setCountry(CODE_TO_COUNTRY[me.country] || 'France');
      }
      // Aller à l'étape 4 UNIQUEMENT si la config est complète (crm_email_active = true)
      if (s.crm_email_active && s.crm_forward_email) {
        setEmail(s.crm_forward_email);
        setStep(4);
      } else if (s.crm_forward_email) {
        // Email sauvegardé mais config incomplète → pré-remplir, rester à l'étape 1
        setEmail(s.crm_forward_email);
      } else if (me?.email) {
        setEmail(me.email);
      }
    }).catch(() => {});
  }, []);

  const startPolling = useCallback(() => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(async () => {
      try {
        const res = await apiClient.get('/crm/gmail-confirmation');
        if (res.data.status === 'received') {
          setConfirmStatus('received');
          if (res.data.url) {
            setConfirmUrl(res.data.url);
            clearInterval(pollingRef.current!);
          }
        }
      } catch {}
    }, 3000);
  }, []);

  useEffect(() => {
    if (step === 3) {
      setConfirmStatus('pending');
      setConfirmUrl(null);
      startPolling();
    } else if (pollingRef.current) clearInterval(pollingRef.current);
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [step, startPolling]);

  const handleSaveEmail = async () => {
    if (!email.trim()) {
      showNotification('Erreur', 'Veuillez ajouter une adresse email', 'error');
      return;
    }
    if (!email.includes('@') || !email.includes('.')) {
      showNotification('Erreur', 'Adresse email invalide', 'error');
      return;
    }
    if (country !== 'France') {
      setShowAutreModal(true);
      return;
    }
    setSaving(true);
    try {
      const countryCode = COUNTRY_TO_CODE[country] || 'FR';
      await apiClient.patch('/crm/settings', {
        crm_forward_email: email.trim().toLowerCase(),
        country: countryCode,
      });
      setStep(2);
    } catch (err: any) {
      const msg: string = err?.message || '';
      if (msg.includes('déjà associé')) {
        setShowEmailTakenModal(true);
      } else {
        showNotification('Erreur', 'Impossible de sauvegarder', 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async () => {
    try {
      await apiClient.patch('/crm/settings', { crm_email_active: true });
    } catch {}
    setStep(4);
  };

  const copyAddress = () => {
    navigator.clipboard.writeText(WEBHOOK_ADDRESS);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const stepLabels = ['Ton email Vinted', 'Ajouter le transfert', 'Validation', 'Activation'];

  const canGoPrev = step > 1 && step < 4;
  const canGoNext = step < TOTAL_STEPS && (step !== 3 || confirmStatus === 'received') && !saving;

  const handlePrev = () => { if (canGoPrev) setStep(s => s - 1); };
  const handleNext = () => {
    if (!canGoNext) return;
    if (step === 1) { handleSaveEmail(); return; }
    if (step === 3 && confirmStatus === 'received') { handleActivate(); return; }
    setStep(s => s + 1);
  };

  const handleConfirmReset = async () => {
    try {
      await apiClient.patch('/crm/settings', { crm_forward_email: '', crm_email_active: false });
    } catch {}
    setEmail('');
    setStep(1);
    setConfirmStatus('pending');
    setConfirmUrl(null);
    setShowResetConfirm(false);
  };

  return (
    <div className="flex flex-col h-full bg-white">

    {/* Modale confirmation suppression */}
    {showResetConfirm && (
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <AlertCircle size={20} className="text-amber-600" />
            </div>
            <h3 className="text-base font-bold text-gray-900">Avant de modifier</h3>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Tu dois <strong>d'abord supprimer l'ancien transfert dans Gmail</strong> sinon Gmail refusera d'en ajouter un nouveau.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-4 space-y-2">
            <p className="text-xs font-bold text-amber-800">À faire dans Gmail avant de continuer :</p>
            <ol className="text-xs text-amber-800 space-y-1 list-decimal list-inside">
              <li>Ouvre les paramètres Gmail → <strong>Transfert et POP/IMAP</strong></li>
              <li>Dans la section <strong>Transfert</strong>, supprime l'adresse <strong className="font-mono">support@smart-resell.com</strong></li>
              <li>Enregistre les modifications</li>
              <li>Reviens ici et clique sur <strong>"Confirmer et recommencer"</strong></li>
            </ol>
          </div>
          <a
            href="https://mail.google.com/mail/u/0/#settings/fwdandpop"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-violet-50 border border-violet-200 text-violet-700 rounded-xl text-sm font-medium hover:bg-violet-100 transition-all mb-4"
          >
            <ExternalLink size={14} /> Ouvrir Gmail → Transfert et POP/IMAP
          </a>
          <div className="flex gap-3">
            <button onClick={() => setShowResetConfirm(false)} className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-200 transition-all">
              Annuler
            </button>
            <button onClick={handleConfirmReset} className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-xl text-sm font-bold hover:bg-red-600 transition-all">
              Confirmer et recommencer
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Modale pays "Autre" */}
    {showAutreModal && (
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <AlertCircle size={20} className="text-orange-500" />
              </div>
              <h3 className="text-base font-bold text-gray-900">Pays non supporté</h3>
            </div>
            <button onClick={() => setShowAutreModal(false)} className="p-1 text-gray-400 hover:text-gray-600">
              <X size={18} />
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-2">
            La synchronisation automatique des emails Vinted n'est actuellement pas disponible pour votre pays.
          </p>
          <p className="text-sm text-gray-500 mb-4">
            Vous pouvez tout de même utiliser le CRM en saisissant vos achats et ventes manuellement.
          </p>
          <button
            onClick={() => setShowAutreModal(false)}
            className="w-full px-4 py-2.5 bg-violet-600 text-white rounded-xl text-sm font-bold hover:bg-violet-700 transition-all"
          >
            Compris
          </button>
        </div>
      </div>
    )}

    {/* Modale email déjà associé à un compte */}
    {showEmailTakenModal && (
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <AlertCircle size={20} className="text-red-500" />
              </div>
              <h3 className="text-base font-bold text-gray-900">Email déjà utilisé</h3>
            </div>
            <button onClick={() => setShowEmailTakenModal(false)} className="p-1 text-gray-400 hover:text-gray-600">
              <X size={18} />
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-2">
            Cette adresse Gmail est déjà associée à un autre compte SmartResell.
          </p>
          <p className="text-sm text-gray-500 mb-4">
            Chaque adresse Gmail Vinted ne peut être connectée qu'à un seul compte. Utilise une autre adresse Gmail ou contacte-nous si tu penses qu'il s'agit d'une erreur.
          </p>
          <button
            onClick={() => setShowEmailTakenModal(false)}
            className="w-full px-4 py-2.5 bg-violet-600 text-white rounded-xl text-sm font-bold hover:bg-violet-700 transition-all"
          >
            Compris
          </button>
        </div>
      </div>
    )}

    {/* ── Bottom-sheet sélection du pays ── */}
    {countryPickerOpen && (
      <div className="fixed inset-0 z-50 flex flex-col justify-end">
        <div
          className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          onClick={() => setCountryPickerOpen(false)}
        />
        <div className="relative bg-white rounded-t-3xl shadow-2xl flex flex-col max-h-[72vh] animate-slideUp">
          {/* Drag handle */}
          <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
            <div className="w-10 h-1 bg-gray-200 rounded-full" />
          </div>
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 flex-shrink-0">
            <div className="flex items-center gap-2">
              <Globe size={16} className="text-violet-500" />
              <h3 className="text-base font-bold text-gray-900">Pays d'activité</h3>
            </div>
            <button
              onClick={() => setCountryPickerOpen(false)}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
            >
              <X size={18} />
            </button>
          </div>
          {/* Liste défilante */}
          <div className="overflow-y-auto flex-1 px-3 py-2">
            {COUNTRIES.map(c => (
              <button
                key={c}
                type="button"
                onClick={() => { setCountry(c); setCountryPickerOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-all ${
                  country === c
                    ? 'bg-violet-50 text-violet-700 font-semibold'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span className="flex-1 text-left">{c}</span>
                {country === c && <Check size={15} className="text-violet-600 flex-shrink-0" />}
              </button>
            ))}
          </div>
          {/* Espace safe area iOS */}
          <div className="h-4 flex-shrink-0" />
        </div>
      </div>
    )}

    <div className="flex-1 overflow-y-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Settings size={22} className="text-violet-600" /> Configuration CRM
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          Relie ton adresse Gmail pour importer tes achats et ventes Vinted automatiquement.
        </p>
      </div>

      {/* Stepper */}
      <div className="flex items-center mb-8">
        {Array.from({ length: TOTAL_STEPS }, (_, i) => (
          <React.Fragment key={i}>
            <div className="flex flex-col items-center gap-1 flex-shrink-0">
              <StepDot n={i + 1} current={step} onClick={() => setStep(i + 1)} />
              <span className={`text-xs font-medium whitespace-nowrap ${step === i + 1 ? 'text-violet-700' : 'text-gray-400'}`}>
                {stepLabels[i]}
              </span>
            </div>
            {i < TOTAL_STEPS - 1 && (
              <div className={`flex-1 h-0.5 mx-3 mb-4 ${i + 1 < step ? 'bg-emerald-400' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Card */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6">

        {/* Étape 1 */}
        {step === 1 && (
          <div>
            <h2 className="text-lg font-bold text-gray-900 mb-1">Ton adresse Gmail Vinted</h2>
            <p className="text-sm text-gray-500 mb-6">
              Indique l'adresse Gmail sur laquelle tu reçois tes emails Vinted et le pays où tu exerces ton activité.
            </p>

            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Adresse Gmail <span className="text-red-400">*</span>
            </label>
            <div className="flex items-center gap-2 border border-gray-200 rounded-xl px-4 py-3 focus-within:ring-2 focus-within:ring-violet-500 focus-within:border-violet-300 transition-all mb-5">
              <Mail size={15} className="text-violet-400 flex-shrink-0" />
              <input
                type="email"
                className="flex-1 text-sm outline-none bg-transparent"
                placeholder="ton-adresse@gmail.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSaveEmail()}
                autoFocus
              />
            </div>

            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Pays d'activité <span className="text-red-400">*</span>
            </label>
            <button
              type="button"
              onClick={() => setCountryPickerOpen(true)}
              className="w-full flex items-center gap-2 border border-gray-200 rounded-xl px-4 py-3 hover:border-violet-300 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-300 transition-all text-left"
            >
              <Globe size={15} className="text-violet-400 flex-shrink-0" />
              <span className="flex-1 text-sm text-gray-800">{country}</span>
              <ChevronDown size={15} className="text-gray-400 flex-shrink-0" />
            </button>
            <p className="text-xs text-gray-400 mt-1.5">
              Détermine les transporteurs disponibles et les fonctionnalités du CRM.
            </p>

            <div className="mt-6 flex items-start gap-2 bg-violet-50 border border-violet-100 rounded-xl px-4 py-3 text-xs text-violet-800">
              <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
              <p>Aucun email ne sera lu sans ton accord. Le transfert est limité aux emails Vinted uniquement.</p>
            </div>
          </div>
        )}

        {/* Étape 2 */}
        {step === 2 && (
          <div>
            <h2 className="text-lg font-bold text-gray-900 mb-1">Ajoute l'adresse de transfert dans Gmail</h2>
            <p className="text-sm text-gray-500 mb-6">
              Dans Gmail, configure un transfert vers notre adresse pour qu'on reçoive tes emails Vinted.
            </p>
            <div className="space-y-4">
              {[
                {
                  n: 1, title: 'Ouvre les paramètres Gmail',
                  content: (
                    <a href="https://mail.google.com/mail/u/0/#settings/fwdandpop" target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm text-violet-600 font-medium hover:underline mt-1">
                      Ouvrir Gmail → Transfert et POP/IMAP <ExternalLink size={13} />
                    </a>
                  ),
                },
                { n: 2, title: 'Clique sur "Ajouter une adresse de transfert"', content: null },
                {
                  n: 3, title: 'Colle cette adresse dans le champ',
                  content: (
                    <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 mt-2">
                      <Mail size={15} className="text-violet-500 flex-shrink-0" />
                      <code className="text-sm text-violet-700 font-mono flex-1">{WEBHOOK_ADDRESS}</code>
                      <button onClick={copyAddress}
                        className="flex items-center gap-1.5 text-xs text-violet-600 font-medium hover:text-violet-800 bg-white border border-violet-200 rounded-lg px-3 py-1.5 transition-all">
                        {copied ? <Check size={13} /> : <Copy size={13} />}
                        {copied ? 'Copié !' : 'Copier'}
                      </button>
                    </div>
                  ),
                },
                {
                  n: 4, title: 'Clique sur "Suivant" puis "Continuer"',
                  content: <p className="text-xs text-gray-400 mt-1">Gmail va envoyer un email de confirmation à notre adresse — il sera validé automatiquement.</p>,
                },
              ].map(s => (
                <div key={s.n} className="flex gap-4">
                  <div className="w-7 h-7 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{s.n}</div>
                  <div className="flex-1"><p className="text-sm font-semibold text-gray-800">{s.title}</p>{s.content}</div>
                </div>
              ))}
            </div>
            <div className="mt-6">
              <p className="text-xs text-gray-400 mb-2">Exemple — à quoi ça ressemble dans Gmail :</p>
              <img src="/gmail-transfer-example.png" alt="Exemple paramètres Gmail transfert" className="w-full rounded-xl border border-gray-200 shadow-sm" />
            </div>
          </div>
        )}

        {/* Étape 3 */}
        {step === 3 && (
          <div>
            <h2 className="text-lg font-bold text-gray-900 mb-1">Validation du transfert</h2>
            <p className="text-sm text-gray-500 mb-6">
              Gmail a envoyé un email de confirmation. Dès qu'on le reçoit, on t'affiche le lien pour confirmer.
            </p>
            <div className={`rounded-2xl border px-5 py-5 transition-all ${confirmStatus === 'received' ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-gray-200'}`}>
              {confirmStatus === 'pending' ? (
                <div className="flex items-center gap-3">
                  <Loader2 size={20} className="text-violet-500 animate-spin flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-gray-800">En attente de validation</p>
                    <p className="text-xs text-gray-400 mt-0.5">Vérification toutes les 3 secondes…</p>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                      <Check size={16} className="text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-emerald-800">Email reçu !</p>
                      <p className="text-xs text-emerald-600 mt-0.5">Clique sur le bouton ci-dessous pour confirmer dans Gmail.</p>
                    </div>
                  </div>
                  {confirmUrl ? (
                    <a href={confirmUrl} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-emerald-300 rounded-xl text-sm text-emerald-700 font-semibold hover:bg-emerald-50 transition-colors">
                      <ExternalLink size={14} /> Ouvrir la confirmation Gmail
                    </a>
                  ) : (
                    <div className="flex items-start gap-2 bg-white border border-emerald-200 rounded-xl px-4 py-3 text-xs text-emerald-800">
                      <AlertCircle size={13} className="flex-shrink-0 mt-0.5 text-emerald-600" />
                      <p>Vérifie ta boîte Gmail — tu as reçu un email de Google avec un lien de confirmation. Clique sur ce lien pour activer le transfert.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
            <details className="mt-4 group">
              <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600 list-none flex items-center gap-1">
                <span className="group-open:rotate-90 transition-transform inline-block">›</span>
                La validation prend trop de temps ?
              </summary>
              <div className="mt-2 text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-xl px-4 py-3">
                Vérifiez que vous avez bien ajouté <strong className="font-mono">{WEBHOOK_ADDRESS}</strong> comme adresse de transfert dans Gmail (étape précédente). L'email peut prendre 1-2 minutes.
              </div>
            </details>
          </div>
        )}

        {/* Étape 4 */}
        {step === 4 && (
          <div>
            <h2 className="text-lg font-bold text-gray-900 mb-1">Active le transfert automatique</h2>
            <p className="text-sm text-gray-500 mb-6">
              Dernière étape : retourne dans Gmail pour activer le transfert vers notre adresse.
            </p>
            <div className="space-y-4">
              {[
                {
                  n: 1, title: 'Retourne dans Gmail',
                  content: (
                    <a href="https://mail.google.com/mail/u/0/#settings/fwdandpop" target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm text-violet-600 font-medium hover:underline mt-1">
                      Paramètres → Transfert et POP/IMAP <ExternalLink size={13} />
                    </a>
                  ),
                },
                {
                  n: 2, title: 'Sélectionne "Transférer une copie à"',
                  content: (
                    <div className="mt-1.5">
                      <p className="text-xs text-gray-400 mb-2">Copie-colle notre adresse dans le champ blanc <em>"Transférer une copie des messages reçus à"</em>.</p>
                      <div className="flex items-center gap-2 bg-violet-50 border border-violet-200 rounded-xl px-3 py-2">
                        <code className="text-sm text-violet-700 font-mono flex-1">{WEBHOOK_ADDRESS}</code>
                        <button onClick={copyAddress}
                          className="flex items-center gap-1.5 text-xs text-violet-600 font-medium hover:text-violet-800 bg-white border border-violet-200 rounded-lg px-3 py-1.5 transition-all flex-shrink-0">
                          {copied ? <Check size={13} /> : <Copy size={13} />}
                          {copied ? 'Copié !' : 'Copier'}
                        </button>
                      </div>
                    </div>
                  ),
                },
                {
                  n: 3, title: 'Enregistre les modifications',
                  content: (
                    <div className="flex items-start gap-2 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2.5 mt-2">
                      <AlertCircle size={13} className="text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-700">N'oublie pas de cliquer sur <strong>"Enregistrer les modifications"</strong> en bas de la page !</p>
                    </div>
                  ),
                },
              ].map(s => (
                <div key={s.n} className="flex gap-4">
                  <div className="w-7 h-7 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{s.n}</div>
                  <div className="flex-1"><p className="text-sm font-semibold text-gray-800">{s.title}</p>{s.content}</div>
                </div>
              ))}
            </div>
            <div className="mt-6">
              <p className="text-xs text-gray-400 mb-2">Exemple — à quoi ça ressemble dans Gmail :</p>
              <img src="/gmail-activation-example.png" alt="Exemple activation transfert Gmail" className="w-full rounded-xl border border-gray-200 shadow-sm" />
            </div>
            <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-2xl px-5 py-4 flex items-center gap-3">
              <span className="text-2xl">🎉</span>
              <div>
                <p className="text-sm font-bold text-emerald-800">Configuration terminée !</p>
                <p className="text-xs text-emerald-700 mt-0.5">
                  Les emails Vinted depuis <strong>{email}</strong> seront importés automatiquement dans ton CRM.
                </p>
              </div>
            </div>
            <button onClick={() => setShowResetConfirm(true)} className="mt-4 text-xs text-gray-400 hover:text-violet-600 hover:underline transition-colors">
              Modifier l'adresse email →
            </button>
          </div>
        )}
      </div>

      {/* FAQ */}
      {step === 4 && (
        <div className="mt-6 bg-gray-50 border border-gray-200 rounded-2xl px-5 py-4">
          <p className="text-xs font-semibold text-gray-700 mb-3">Questions fréquentes</p>
          <div className="space-y-3">
            {[
              { q: 'Mes emails privés seront-ils lus ?', a: 'Non. Seuls les emails Vinted (@vinted.fr / @vinted.com) sont transférés grâce au filtre Gmail.' },
              { q: 'Les emails anciens seront-ils importés ?', a: 'Non, seuls les nouveaux emails après configuration. Tu peux ajouter des achats anciens manuellement.' },
              { q: 'Comment modifier mon adresse ?', a: 'Clique sur "Modifier l\'adresse email" en bas de cette page pour recommencer la configuration.' },
            ].map((f, i) => (
              <div key={i}>
                <p className="text-xs font-medium text-gray-700">→ {f.q}</p>
                <p className="text-xs text-gray-500 mt-0.5">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>

    {/* Barre de navigation */}
    {step < 4 && (
      <div className="px-6 py-4 bg-white border-t border-gray-200 flex items-center justify-between">
        <button
          onClick={handlePrev}
          disabled={!canGoPrev}
          className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          <ChevronLeft size={20} />
          <span>Précédent</span>
        </button>
        <button
          onClick={handleNext}
          disabled={!canGoNext}
          className="px-6 py-3 bg-gradient-to-r from-violet-500 to-violet-600 text-white rounded-xl hover:from-violet-600 hover:to-violet-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 font-bold"
        >
          <span>{step === 3 ? 'Activer' : 'Suivant'}</span>
          <ChevronRight size={20} />
        </button>
      </div>
    )}
    </div>
  );
};

export default CrmConfiguration;
